package com.siriusxm.pia.operations.dashboard

import androidx.compose.runtime.Composable
import com.siriusxm.pia.components.Box
import com.siriusxm.pia.components.BoxConfig
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

/**
 * A single dashboard cell within a [Row].
 *
 * A widget occupies [span] of 12 columns when the row has room for it, dividing the available
 * width proportionally with its siblings. As the viewport narrows past [minWidth] the widget
 * wraps to its own full-width line — this is what makes the dashboard responsive without any
 * global breakpoint. Each widget reflows independently based on its own [minWidth].
 *
 * The widget's card chrome is the shared [Box] component, so a titled widget looks exactly like
 * a box elsewhere in the app and inherits its full configuration surface via [config] — most
 * usefully a header `menu { }` (e.g. a "View in DataDog" action). The box is rendered with
 * `fill = true` so all widgets in a row share the height of the tallest, letting short content
 * (like a [StatCard]) center vertically.
 *
 * The [content] slot accepts any composable, so first-class widgets such as
 * [com.siriusxm.pia.operations.components.MetricsChart] and
 * [com.siriusxm.pia.operations.components.LogsViewer] are dropped in directly.
 *
 * @param span number of columns (1..12) this widget occupies when space allows.
 * @param minWidth minimum width in pixels before the widget expands to full row width.
 * @param title optional card header title.
 * @param subtitle optional secondary line shown under the title (the box header instruction).
 * @param paddedContent when true (default) the content area gets standard padding; set false
 *   for content that manages its own padding (e.g. tables, log lists).
 * @param config optional configuration forwarded to the underlying [Box] — e.g. `menu { }` or
 *   `action { }` header buttons.
 * @param content the widget body.
 */
@Composable
fun Widget(
    span: Int = 12,
    minWidth: Int = 240,
    title: String? = null,
    subtitle: String? = null,
    paddedContent: Boolean = true,
    config: BoxConfig.() -> Unit = {},
    content: @Composable () -> Unit
) {
    val effectiveSpan = span.coerceIn(1, 12)

    Div({
        classes(DashboardStyles.widgetCell)
        style {
            // flex-grow drives the proportional column share; flex-basis is 0 so grow ratios
            // alone determine the split. min-width triggers wrap-to-full-width when narrow.
            flexGrow(effectiveSpan)
            minWidth(minWidth.px)
        }
    }) {
        Box({
            this.title = title
            subtitle?.let { sub -> header({ instruction = sub }) }
            this.paddedContent = paddedContent
            fill = true
            config()
        }) {
            content()
        }
    }
}
